import React, { useEffect, useState } from "react";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext.tsx";
import type { GatepassFlowData } from "@features/gatepass/components/flow/ClaimGatepass";
import GatepassFlowButtons from "@features/gatepass/components/flow/GatepassFlowButtons";
import { GATEPASS_STATUSES, pickTaskForFlowRole } from "@features/gatepass/components/flow/utils";
import ApproveGatepassDrawer from "@features/gatepass/components/form/ApproveGatepassDrawer";
import RejectGatepassDrawer from "@features/gatepass/components/form/RejectGatepassDrawer";
import GatepassDetailsSection, {
  type FieldEntry,
} from "@features/gatepass/components/preview/GatepassDetailsSection";
import GatepassProfileSection from "@features/gatepass/components/preview/GatepassProfileSection";
import {
  GATEPASS_TYPES,
  getGatepassCategoryTranslationKey,
  normalizeGatepassTaskDecisionAttachment,
} from "@features/gatepass/components/utils.tsx";
import type { Company, VisaType } from "@features/port-security/components/SecurityClearence.types";
import { useDownloadSecurityClearanceAttachments } from "@features/port-security/hooks/useDownloadSecurityClearanceAttachments";
import AttachmentsComponent from "@features/vessel-call/vessel-call-status/components/AttachmentComponent/AttachmentsComponent";
import { useFormattedReference } from "@hooks/useFormattedReference";
import PageLayout from "@layouts/PageLayout";
import { getDocumentsWithToken } from "@services/azureDataApi";
import {
  type GatePassTask,
  type GatepassTaskDecisionAttachment,
  type TruckCargo,
} from "@services/gatepass/types";
import { getGatepassPrintDocument } from "@services/gatepassApi.ts";
import { getGatepassById } from "@services/gatepassDataApi";
import type { Gates } from "@services/reference-data/types";
import {
  getGatepassRegistrationEmirateLabel,
  REFERENCE_DATA_TYPES,
} from "@services/referenceDataApi";
import { SasTokenPrefix } from "@services/user/types";
import type { PortCode, UnitsOfMeasurement } from "@services/vessel-call/types";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { printRequestedTemplate } from "@utils/printRequestedTemplate";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

const service = "bff-gatepass";
const TEST_ID_BASE = "gatepass-request-preview";

const STATUS_BADGE_MAP: Record<string, "success" | "error" | "warning"> = {
  APPROVED: "success",
  REJECTED: "error",
  PROCESSING: "warning",
  AWAITING_FOTT_APPROVAL: "warning",
  AWAITING_SPONSOR_COMPANY_APPROVAL: "warning",
  AWAITING_SECURITY_DEPT_APPROVAL: "warning",
};
const parseTimeMs = (raw?: string | null): number => {
  if (!raw?.trim()) return -1;
  const n = new Date(raw).getTime();
  return Number.isFinite(n) ? n : -1;
};

const firstNonEmptyTrimmed = (...vals: (string | null | undefined)[]): string | null => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return null;
};

const historyItemDecisionStatus = (item: {
  status?: string;
  taskDecision?: { status?: string } | null;
}): string => {
  const td = String(item.taskDecision?.status ?? "").trim();
  if (td) return td.toUpperCase();
  return String(item.status ?? "")
    .trim()
    .toUpperCase();
};

type TaskDecisionFileSource = {
  attachments?: GatepassTaskDecisionAttachment[];
  internalAttachments?: GatepassTaskDecisionAttachment[];
  rejectionReasonAttachments?: GatepassTaskDecisionAttachment[];
  internal_attachments?: GatepassTaskDecisionAttachment[];
  rejection_reason_attachments?: GatepassTaskDecisionAttachment[];
};

// Collect approve/reject files
const normalizeTaskDecisionFiles = (
  decision: TaskDecisionFileSource | null | undefined,
  kind: "APPROVE" | "REJECT",
): Array<{ url: string; type: string; originalFileName?: string | null }> => {
  if (!decision) return [];
  const out: Array<{ url: string; type: string; originalFileName?: string | null }> = [];
  for (const att of decision.attachments ?? []) {
    out.push(
      normalizeGatepassTaskDecisionAttachment(
        att,
        kind === "APPROVE" ? "APPROVE_ATTACHMENT" : "REJECT_ATTACHMENT",
      ),
    );
  }
  if (kind === "APPROVE") {
    const internal = [
      ...(decision.internalAttachments ?? []),
      ...(decision.internal_attachments ?? []),
    ];
    for (const att of internal) {
      out.push(normalizeGatepassTaskDecisionAttachment(att, "INTERNAL_ATTACHMENT"));
    }
  } else {
    const rejection = [
      ...(decision.rejectionReasonAttachments ?? []),
      ...(decision.rejection_reason_attachments ?? []),
    ];
    for (const att of rejection) {
      out.push(normalizeGatepassTaskDecisionAttachment(att, "REJECT_ATTACHMENT"));
    }
  }
  return out;
};

// Check whether tasksHistory is oldest or newest first
const isHistoryOldestFirst = <
  T extends {
    createdOn?: string;
  },
>(
  history: T[],
): boolean | null => {
  if (history.length < 2) return null;
  const first = parseTimeMs(history[0]?.createdOn);
  const last = parseTimeMs(history[history.length - 1]?.createdOn);
  if (first < 0 || last < 0) return null;
  return first <= last;
};

/**
 * Latest taskDecision for APPROVED/REJECTED: case-insensitive status, sort by real timestamps when
 * present; when all timestamps missing, infer list order from history createdOn or assume append-only
 * (last index wins).
 */
const getLatestHistoryTask = <
  T extends {
    status?: string;
    createdOn?: string;
    modifiedOn?: string;
    taskDecision?: {
      status?: string;
      reason?: string | null;
      notes?: string | null;
      timestamp?: string;
      attachments?: GatepassTaskDecisionAttachment[];
      internalAttachments?: GatepassTaskDecisionAttachment[];
      rejectionReasonAttachments?: GatepassTaskDecisionAttachment[];
    } | null;
  },
>(
  history: T[] | null | undefined,
  decisionStatus: "APPROVED" | "REJECTED",
) => {
  if (!Array.isArray(history) || history.length === 0) return undefined;
  const want = decisionStatus.toUpperCase();
  const matches = history
    .map((item, index) => ({ item, index }))
    .filter((x) => historyItemDecisionStatus(x.item) === want);
  if (matches.length === 0) return undefined;

  const timeMsFor = (item: T) => {
    const raw = item.modifiedOn ?? item.createdOn ?? item.taskDecision?.timestamp ?? "";
    return parseTimeMs(raw);
  };

  const anyValidTime = matches.some((m) => timeMsFor(m.item) >= 0);
  if (anyValidTime) {
    matches.sort((a, b) => {
      const tb = timeMsFor(b.item);
      const ta = timeMsFor(a.item);
      if (tb !== ta) return tb - ta;
      return b.index - a.index;
    });
    return matches[0].item ?? undefined;
  }

  const oldestFirst = isHistoryOldestFirst(history);
  if (oldestFirst === true) {
    matches.sort((a, b) => b.index - a.index);
    return matches[0].item ?? undefined;
  }
  if (oldestFirst === false) {
    matches.sort((a, b) => a.index - b.index);
    return matches[0].item ?? undefined;
  }
  matches.sort((a, b) => b.index - a.index);
  return matches[0].item ?? undefined;
};

const GatepassRequestPreview: React.FC = () => {
  type GenericReferenceItem = Record<string, unknown>;

  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { notify } = useNotification();

  const [openDrawers, setOpenDrawers] = useState<Record<string, boolean>>({
    approveDrawer: false,
    rejectDrawer: false,
  });
  const [showDecisionDetailsDrawer, setShowDecisionDetailsDrawer] = useState(false);
  const [fakeStatus, setFakeStatus] = useState<string | null>(null);

  const handleOpenDrawer = (drawerName: string) => {
    setOpenDrawers((prev) => ({ ...prev, [drawerName]: true }));
  };

  const handleCloseDrawer = (drawerName: string) => {
    setOpenDrawers((prev) => ({ ...prev, [drawerName]: false }));
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["gatepass-request-preview", id],
    queryFn: () => getGatepassById(service, id as string),
    enabled: !!id,
    refetchOnReconnect: true,
    refetchInterval: 60_000,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const { options: allCompanies } = useFormattedReference<Company>(
    REFERENCE_DATA_TYPES.COMPANIES,
    "bff-gatepass",
  );

  const { options: visaTypes } = useFormattedReference<VisaType>(
    REFERENCE_DATA_TYPES.VISA_TYPES,
    service,
  );

  const { options: gates } = useFormattedReference<Gates>(REFERENCE_DATA_TYPES.GATES, service);

  const { options: ports } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORT_CODES,
    service,
  );
  const { options: cargosRaw } = useFormattedReference<GenericReferenceItem>(
    REFERENCE_DATA_TYPES.CARGOS,
    "bff-gatepass",
  );
  const { options: vehicleTypesRaw } = useFormattedReference<GenericReferenceItem>(
    REFERENCE_DATA_TYPES.VEHICLE_TYPES,
    "bff-gatepass",
  );
  const { options: unitsOfMeasurementRaw } = useFormattedReference<UnitsOfMeasurement>(
    REFERENCE_DATA_TYPES.UNITS_OF_MEASUREMENT,
    "bff-gatepass",
  );

  const { handlePreview, downloadAttachment } = useDownloadSecurityClearanceAttachments({ t });

  const photoUrl = data?.details?.photoUrl ?? data?.photoUrl;
  const { data: photoBlob } = useQuery({
    queryKey: ["gatepass-request-photo", photoUrl],
    queryFn: () =>
      getDocumentsWithToken(photoUrl as string, SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE),
    enabled: !!photoUrl,
  });

  const [profileImgUrl, setProfileImgUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!photoBlob || !(photoBlob instanceof Blob)) return;
    const blobUrl = URL.createObjectURL(photoBlob);
    setProfileImgUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [photoBlob]);

  const handlePrint = async () => {
    await printRequestedTemplate({
      id,
      service,
      requestTemplateDocument: getGatepassPrintDocument,
      notify,
      t,
    });
  };

  // Show full-page loader only until the first successful load. Background refetches must not
  // blank the page (avoids flicker after approve/reject while `isFetching` is true).
  if (isLoading) {
    return <LoadingOverlay loading />;
  }

  if (isError || !data) {
    return <ErrorHandler error={error} testId={`${TEST_ID_BASE}-error-handler`} />;
  }

  const isNormalGatepass = data.gatepassCategory?.toLowerCase() === GATEPASS_TYPES.NORMAL;
  const isOffshoreGatepass = data.gatepassCategory?.toLowerCase() === GATEPASS_TYPES.OFFSHORE;
  const isTruckGatepass = data.gatepassCategory?.toLowerCase() === GATEPASS_TYPES.TRUCK;

  const serialNumber = data?.serialNumber ?? "-";
  const gatepassSequence = data?.gatepassSequence ?? "-";
  const gatepassCategory = data?.gatepassCategory ?? "-";
  const normalizedTruckType = String(data?.details?.truckType ?? "")
    .trim()
    .toUpperCase();
  const effectiveStatus = (fakeStatus || data?.status) ?? "-";
  const status = effectiveStatus;
  const gatepassTypeLabel =
    isTruckGatepass && normalizedTruckType === "CARGO"
      ? t("gatepass.cargo", { defaultValue: "Cargo" })
      : t(getGatepassCategoryTranslationKey(gatepassCategory), {
          defaultValue: gatepassCategory,
        });
  const statusNorm = String(status ?? "")
    .trim()
    .toUpperCase();
  const statusLabel = statusNorm
    ? t(`gatepass.status-labels.${statusNorm}`, { defaultValue: statusNorm })
    : t("common.unknown");
  const badgeType = STATUS_BADGE_MAP[statusNorm] ?? "warning";
  const applicant = data?.securityClearance?.applicant;

  const fullNameEn =
    applicant?.fullNameEnglish ?? data?.visitor?.fullNameEnglish ?? data?.submittedBy ?? "-";
  const fullNameAr = applicant?.fullNameArabic ?? "-";
  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : format(parsed, "dd/MM/yyyy");
  };
  const dateOfBirth = formatDate(applicant?.dateOfBirth);
  const passportNumber = applicant?.passportNumber ?? data?.visitor?.passportNumber ?? "-";
  const passportExpiryDate = formatDate(applicant?.passportExpiryDate);
  const gatepassNo = gatepassSequence ?? "-";
  const expectedEntryDate = formatDate(data?.expectedEntryDate);
  const dateOfIssue = formatDate(data?.submittedAt);
  const dateOfExpiry = formatDate(data?.expiryDate);
  const visaExpiryDate = formatDate(data?.securityClearance?.residenceStatus?.expiryDate);
  const visaTypeCode = data?.securityClearance?.residenceStatus?.visaType;
  const getVisaTypeLabel = (code?: string | null) => {
    if (!code) return "-";
    const match = visaTypes.find((visa) => visa.typeCode === code);
    return match?.typeName ?? code;
  };
  const entryPurpose = data?.visitor?.entryPurpose;
  const securityDuration = formatDate(data?.securityClearance?.securityClearanceExpiryDate);
  const visitorCompanyEnglish = data?.visitorCompanyEnglish ?? "-";
  const visitorCompanyArabic = data?.visitorCompanyArabic ?? "-";
  const hasCamera = data?.details?.hasCamera;
  const cameraSn = data?.details?.cameraDetails?.cameraSerialNumber ?? "-";
  const cameraModel = data?.details?.cameraDetails?.cameraModel ?? "-";
  const uidNumber = data?.securityClearance?.uidNumber ?? "-";
  const occupationEnglish = applicant?.occupationEnglish ?? "-";
  const occupationArabic = applicant?.occupationArabic ?? "-";
  const nationalityEnglish = applicant?.presentNationality ?? "-";
  const nationalityArabic = applicant?.presentNationalityArabic ?? "-";
  const phoneNumber = data?.visitor?.mobileNumber ?? "-";
  const driverLicenseNumber = data?.visitor?.driverLicenseNumber ?? "-";
  const documents = data?.securityClearance?.documents ?? [];
  const passportFront = documents.find((item) => item.type.includes("PASSPORT_FRONT"));
  const passportBack = documents.find((item) => item.type.includes("PASSPORT_BACK"));
  const emiratesIdFront = documents.find((item) => item.type.includes("EMIRATES_ID_FRONT"));
  const emiratesIdBack = documents.find((item) => item.type.includes("EMIRATES_ID_BACK"));
  const visa = documents.filter((item) => item.type === "VISA");
  const deliveryNotes = data?.details?.deliveryNotes ?? [];
  const gateCode = data?.gate ?? "";
  const gateMatch = gates.find((gate) => gate.gateNo === gateCode);
  const gateLabel = (gateMatch?.gateDesc ?? gateCode) || "-";
  const portCode = data?.port ?? "";
  const portMatch = ports.find((port) => String(port.portCode).trim() === portCode.trim());
  const portLabel = (portMatch?.portName ?? portCode) || "-";
  const sponsorCompanyCode = data?.sponsorCompany ?? "";
  const trimUppercaseValue = (value?: unknown) =>
    String(value ?? "")
      .trim()
      .toUpperCase();
  const findCompanyByCode = (rawCode?: string | null) => {
    const normalizedCode = trimUppercaseValue(rawCode);
    if (!normalizedCode) return undefined;
    return allCompanies.find(
      (company) => trimUppercaseValue(company.companyCode) === normalizedCode,
    );
  };
  const sponsorCompanyMatch = findCompanyByCode(sponsorCompanyCode);
  const extractCompanyName = (companyMatch: Company) => {
    return i18n.language === "ar"
      ? companyMatch.companyNameArabic || companyMatch.companyNameEnglish
      : companyMatch.companyNameEnglish || companyMatch.companyNameArabic;
  };
  const sponsorCompanyName = sponsorCompanyMatch
    ? extractCompanyName(sponsorCompanyMatch)
    : sponsorCompanyCode || "-";
  const submittedByRole = String(data?.submittedByRole ?? "")
    .trim()
    .toLowerCase();
  const isSponsorCompanyOrVendorMaster =
    submittedByRole === "vendor-master" || submittedByRole === "licensed-company-agent";
  const submittedByUsername = String(data?.submittedByUsername ?? "").trim();
  const submittedByCompanyCode = String(data?.submittedBy ?? "").trim();
  const submittedByCompanyMatch = findCompanyByCode(submittedByCompanyCode);
  const submittedByCompanyName = submittedByCompanyMatch
    ? extractCompanyName(submittedByCompanyMatch)
    : submittedByCompanyCode || "-";
  const resolveVehicleTypeLabel = (rawValue?: string | null) => {
    const normalizedRawValue = String(rawValue ?? "")
      .trim()
      .toUpperCase();
    if (!normalizedRawValue) return "-";

    const vehicleTypeMatch = vehicleTypesRaw.find((entry) => {
      const record = entry as Record<string, unknown>;
      return Object.values(record).some(
        (value) => typeof value === "string" && value.trim().toUpperCase() === normalizedRawValue,
      );
    }) as Record<string, unknown> | undefined;

    if (!vehicleTypeMatch) return "-";

    const preferredLabelFields = [
      "vehicleTypeDescription",
      "description",
      "label",
      "name",
      "typeDescription",
    ];
    for (const field of preferredLabelFields) {
      const candidate = String(vehicleTypeMatch[field] ?? "").trim();
      if (candidate) return candidate;
    }

    const firstStringValue = Object.values(vehicleTypeMatch).find(
      (value): value is string => typeof value === "string" && value.trim().length > 0,
    );
    return firstStringValue?.trim() || "-";
  };
  const resolveVehicleCategoryLabel = (rawValue?: string | null) => {
    const normalizedRawValue = String(rawValue ?? "")
      .trim()
      .toUpperCase();
    if (!normalizedRawValue) return "-";

    const categoryLabelMap: Record<string, string> = {
      S: "Small",
      M: "Medium",
      L: "Large",
    };

    return categoryLabelMap[normalizedRawValue] ?? "-";
  };
  const skipSponsorCompanyApproval =
    sponsorCompanyCode.trim() !== "" &&
    submittedByCompanyCode.trim() !== "" &&
    sponsorCompanyCode.trim() === submittedByCompanyCode.trim();

  const submittedByDisplay =
    isSponsorCompanyOrVendorMaster && submittedByUsername && submittedByCompanyName !== "-"
      ? `${submittedByUsername} - ${submittedByCompanyName}`
      : submittedByUsername || "-";
  const referenceValue = data.reference?.trim() || "-";
  const latestApprovedHistoryEntry = data.tasksHistory
    ?.slice()
    .reverse()
    .find((item) => item.taskDecision?.status === "APPROVED");

  const isSecurityDeptSubmittedByRole = (role?: string | null) => {
    const normalized = String(role ?? "")
      .trim()
      .toUpperCase()
      .replace(/-/g, "_");
    return [
      "SECURITY_OPERATOR",
      "SECURITY_SUPERVISOR",
      "SECURITY_DEPT",
      "SECURITY_DEPARTMENT",
    ].includes(normalized);
  };

  const latestSecurityApprovedHistoryEntry =
    status === "APPROVED"
      ? (data.tasksHistory
          ?.slice()
          .reverse()
          .find(
            (item) =>
              item.taskDecision?.status === "APPROVED" &&
              isSecurityDeptSubmittedByRole(item.taskDecision?.submittedByRole),
          ) ?? latestApprovedHistoryEntry)
      : undefined;

  const issuedByApprovedBy =
    status === "APPROVED"
      ? latestSecurityApprovedHistoryEntry?.taskDecision?.submittedBy?.trim() || "-"
      : "-";
  const attachmentsLabel = t("gatepass.attachments");
  const locations = data?.details?.locations ?? [];
  const locationFields = locations.flatMap((location, index) => {
    let locationLabel = t("gatepass.location-entry");

    if (index === 0) locationLabel = t("gatepass.primary-location");
    if (index > 0) locationLabel = `${locationLabel} ${index + 1}`;

    return [
      { label: locationLabel, value: location.locationName ?? "-" },
      { label: t("gatepass.entry-purpose"), value: location.entryPurpose ?? "-" },
    ];
  });
  const vesselDetails = data?.details?.vesselDetails ?? [];
  const vesselFields = vesselDetails.flatMap((vessel, index) => {
    const vesselLabel = `${t("gatepass.vessel-imo")} ${index + 1}`;

    return [
      { label: vesselLabel, value: vessel?.imoNumber?.toString() ?? "-" },
      { label: t("gatepass.vessel-name"), value: vessel?.name ?? "-" },
    ];
  });

  const cargos: TruckCargo[] = data?.details?.cargos ?? [];
  const firstNonEmptyString = (...values: unknown[]) => {
    for (const value of values) {
      const trimmed = String(value ?? "").trim();
      if (trimmed) return trimmed;
    }
    return "";
  };

  const resolveCargoReference = (cargoItem: TruckCargo) => {
    const cargoCode = firstNonEmptyString(cargoItem.cargoCode);
    const cargoTypeCode = firstNonEmptyString(cargoItem.cargoTypeCode);

    const matchByCargoCode = cargosRaw.find((entry) => {
      const entryCode = trimUppercaseValue(entry?.cargoCode);
      return entryCode && entryCode === trimUppercaseValue(cargoCode);
    }) as Record<string, unknown> | undefined;

    const matchByCargoTypeCode = cargosRaw.find((entry) => {
      const topLevelTypeCode = trimUppercaseValue(entry?.cargoTypeCode);
      const nestedCargoType = entry?.cargoType as Record<string, unknown> | undefined;
      const entryTypeCode = trimUppercaseValue(nestedCargoType?.cargoTypeCode);
      const normalizedWantedTypeCode = trimUppercaseValue(cargoTypeCode);
      return (
        normalizedWantedTypeCode &&
        (entryTypeCode === normalizedWantedTypeCode ||
          topLevelTypeCode === normalizedWantedTypeCode)
      );
    }) as Record<string, unknown> | undefined;

    const cargoRef = matchByCargoCode ?? matchByCargoTypeCode;
    const nestedCargoType = cargoRef?.cargoType as Record<string, unknown> | undefined;

    return {
      cargoDescription: firstNonEmptyString(cargoRef?.cargoDescription),
      cargoTypeDescription: firstNonEmptyString(
        cargoRef?.cargoTypeDescription,
        nestedCargoType?.cargoTypeDescription,
      ),
    };
  };

  const resolveUnitOfMeasurementLabel = (value?: string | null) => {
    const normalized = String(value ?? "")
      .trim()
      .toUpperCase();
    if (!normalized) return "-";

    const unitMatch = unitsOfMeasurementRaw.find((unit) => {
      const unitCode = String(unit.unitCode ?? "")
        .trim()
        .toUpperCase();
      const unitDescription = String(unit.unitDescription ?? "")
        .trim()
        .toUpperCase();
      return unitCode === normalized || unitDescription === normalized;
    });

    return unitMatch?.unitDescription?.trim() || "-";
  };

  const translateBoolean = (bool: boolean | null | undefined) => {
    if (bool === true) return t("yes");
    if (bool === false) return t("no");
    return "-";
  };

  const buildCargoRowFields = (cargo: TruckCargo) => {
    const cargoReference = resolveCargoReference(cargo);
    const hazardousValue = cargo?.hazardous ?? cargo?.hazardCargo;
    const hazardValue = translateBoolean(hazardousValue);

    return [
      {
        label: t("gatepass.cargo-type", { defaultValue: "Cargo Type" }),
        value: cargoReference.cargoTypeDescription || "-",
      },
      {
        label: t("gatepass.cargo", { defaultValue: "Cargo" }),
        value: cargoReference.cargoDescription || "-",
      },
      {
        label: t("gatepass.hazard-cargo", { defaultValue: "Hazard Cargo" }),
        value: hazardValue,
      },
      {
        label: t("gatepass.quantity", { defaultValue: "Quantity" }),
        value: cargo?.quantity != null ? String(cargo.quantity) : "-",
      },
      {
        label: t("gatepass.unit-of-measurement", { defaultValue: "Unit of measurement" }),
        value: resolveUnitOfMeasurementLabel(cargo?.unitOfMeasurement),
      },
    ];
  };

  const resolveFirstTruckCargoValue = (
    pick: (cargo: TruckCargo) => string | number | null | undefined,
  ): string => {
    for (const cargo of cargos) {
      const raw = pick(cargo);
      if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
        return String(raw);
      }
    }
    return "";
  };
  const truckJobDetails = data?.details?.jobDetails;
  const resolveTruckJobDetailValue = (
    jobDetailKey: keyof NonNullable<typeof truckJobDetails>,
    cargoPick: (cargo: TruckCargo) => string | number | null | undefined,
  ): string => {
    const fromJobDetails = truckJobDetails?.[jobDetailKey];
    if (
      fromJobDetails !== undefined &&
      fromJobDetails !== null &&
      String(fromJobDetails).trim() !== ""
    ) {
      return String(fromJobDetails);
    }
    return resolveFirstTruckCargoValue(cargoPick);
  };
  const resolveCargoHandlingPartyLabel = (value?: string | null) => {
    const companyCode = String(value ?? "").trim();
    if (!companyCode) return "-";

    const companyMatch = findCompanyByCode(companyCode);
    if (!companyMatch) return "-";

    if (i18n.language === "ar") {
      return companyMatch.companyNameArabic || companyMatch.companyNameEnglish || "-";
    }

    return companyMatch.companyNameEnglish || companyMatch.companyNameArabic || "-";
  };
  const jobDetailsFields = isTruckGatepass
    ? [
        ...(data.details?.truckType === "CARGO" && data.details.cargoDestination === "PORT"
          ? [
              {
                label: t("gatepass.deliver-to", { defaultValue: "Deliver To" }),
                value: data.details?.jobDetails?.deliverTo || "-",
              },
            ]
          : []),
        {
          label: t("gatepass.destination-vcn", { defaultValue: "Destination VCN" }),
          value:
            resolveTruckJobDetailValue("destinationVcn", (cargo) => cargo.destinationVcn) || "-",
        },
        {
          label: t("gatepass.vessel-name", { defaultValue: "Vessel Name" }),
          value: resolveTruckJobDetailValue("vesselName", (cargo) => cargo.vesselName) || "-",
        },
        {
          label: t("gatepass.service-number", { defaultValue: "Service Number" }),
          value: resolveTruckJobDetailValue("serviceNumber", (cargo) => cargo.serviceNumber) || "-",
        },
        {
          label: t("gatepass.cargo-handling-party", { defaultValue: "Cargo Handling Party" }),
          value: resolveCargoHandlingPartyLabel(
            resolveTruckJobDetailValue("cargoHandlingParty", (cargo) => cargo.cargoHandlingParty),
          ),
        },
        {
          label: t("gatepass.job-number", { defaultValue: "Job Number" }),
          value: resolveTruckJobDetailValue("jobNumber", (cargo) => cargo.jobNumber) || "-",
        },
        {
          label: t("gatepass.job-description", { defaultValue: "Job Description" }),
          value:
            resolveTruckJobDetailValue("jobDescription", (cargo) => cargo.jobDescription) || "-",
        },
        {
          label: t("gatepass.job-start-date", { defaultValue: "Job Start Date" }),
          value: (() => {
            const raw = resolveTruckJobDetailValue("jobStartDate", (cargo) => cargo.jobStartDate);
            return raw ? formatDate(raw) : "-";
          })(),
        },
        {
          label: t("gatepass.job-end-date", { defaultValue: "Job End Date" }),
          value: (() => {
            const raw = resolveTruckJobDetailValue("jobEndDate", (cargo) => cargo.jobEndDate);
            return raw ? formatDate(raw) : "-";
          })(),
        },
      ]
    : [];
  const cargoDestinationFields = isTruckGatepass
    ? [
        {
          label: t("gatepass.cargo-destination", {
            defaultValue: "Cargo destination (port/vessel)",
          }),
          value:
            data?.cargoDestination?.trim() ||
            data?.details?.cargoDestination?.trim() ||
            resolveFirstTruckCargoValue((cargo) => cargo.cargoDestination) ||
            "-",
        },
      ]
    : [];
  const resolveTruckTypeLabel = (value?: string | null) => {
    const normalized = String(value ?? "TRUCK")
      .trim()
      .toUpperCase();
    if (normalized === "CARGO") {
      return t("gatepass.cargo", { defaultValue: "Cargo" });
    }
    return t("gatepass.truck-type-truck", { defaultValue: "Truck" });
  };
  const createCameraFields = () => {
    const cameraFields: FieldEntry[] = [
      {
        label: t("gatepass.carrying-camera"),
        value: hasCamera ? t("yes") : t("no"),
      },
    ];
    if (hasCamera) {
      cameraFields.push(
        {
          label: t("gatepass.camera-sn"),
          value: cameraSn,
        },
        {
          label: t("gatepass.camera-model"),
          value: cameraModel,
        },
      );
    }

    return cameraFields;
  };
  const vehicleRightFields = [
    {
      label: t("gatepass.registration-emirate"),
      value: getGatepassRegistrationEmirateLabel(data?.vehicle?.registrationEmirate) ?? "-",
    },
    {
      label: t("gatepass.vehicle-type"),
      value: resolveVehicleTypeLabel(data?.vehicle?.type),
    },
    { label: t("gatepass.vehicle-colour"), value: data?.vehicle?.colour ?? "-" },
    {
      label: t("gatepass.category"),
      value: resolveVehicleCategoryLabel(data?.vehicle?.category),
    },
    { label: t("gatepass.plate-number"), value: data?.vehicle?.plateNumber ?? "-" },
    ...(isOffshoreGatepass ? createCameraFields() : []),
  ];
  const cargoTypeTitle = (index: number) =>
    t("gatepass.cargo-type-header", {
      n: index,
      defaultValue: `Cargo type ${index}`,
    });
  const constructCargoGridItems = () => {
    if (cargos.length > 0)
      return cargos.map((cargo, index) => ({
        title: cargoTypeTitle(index + 1),
        fields: buildCargoRowFields(cargo),
      }));

    return [
      {
        title: cargoTypeTitle(1),
        fields: [
          {
            label: t("gatepass.cargo-type", { defaultValue: "Cargo Type" }),
            value: "-",
          },
        ],
      },
    ];
  };
  const truckCargoGridItems = isTruckGatepass ? constructCargoGridItems() : [];
  const isRejected = statusNorm === GATEPASS_STATUSES.REJECTED;
  const isApproved = statusNorm === GATEPASS_STATUSES.APPROVED;
  const isAwaitingFottApproval = statusNorm === GATEPASS_STATUSES.AWAITING_FOTT_APPROVAL;
  const isAwaitingSponsorCompanyApproval =
    statusNorm === GATEPASS_STATUSES.AWAITING_SPONSOR_COMPANY_APPROVAL;
  const isAwaitingSecurityDeptApproval =
    statusNorm === GATEPASS_STATUSES.AWAITING_SECURITY_DEPT_APPROVAL;
  const usesApprovedDecisionDetails =
    isApproved ||
    isAwaitingFottApproval ||
    isAwaitingSponsorCompanyApproval ||
    isAwaitingSecurityDeptApproval;

  // @TODO -Check with Backend
  // API may return task (singular) or tasks (array).
  const activeTask: GatePassTask | undefined =
    pickTaskForFlowRole<GatePassTask>(status, data?.task ?? undefined, data?.tasks ?? undefined) ??
    data?.task ??
    data?.tasks?.[0] ??
    undefined;
  const latestRejectedTask = getLatestHistoryTask(data?.tasksHistory ?? [], "REJECTED");
  const latestApprovedTask = getLatestHistoryTask(data?.tasksHistory ?? [], "APPROVED");
  const latestRejectedDecision = latestRejectedTask?.taskDecision;
  const latestApprovedDecision = latestApprovedTask?.taskDecision;

  const rejectReason = (() => {
    const responseReason = firstNonEmptyTrimmed(data.rejectionReason);
    if (responseReason) return responseReason;

    if (latestRejectedDecision) {
      return firstNonEmptyTrimmed(latestRejectedDecision.reason, latestRejectedDecision.notes);
    }
    return null;
  })();

  const rejectAttachments = (() => {
    if (latestRejectedDecision) {
      return normalizeTaskDecisionFiles(latestRejectedDecision, "REJECT");
    }
    return [];
  })();

  const approvedNotesResolved = (() => {
    if (latestApprovedDecision) {
      const fromDecision = firstNonEmptyTrimmed(
        latestApprovedDecision.reason,
        latestApprovedDecision.notes,
      );
      if (fromDecision) return fromDecision;
    }
    const hid = latestApprovedTask?.id;
    if (hid) {
      const candidates = [data?.task, ...(data?.tasks ?? [])].filter(
        (t): t is GatePassTask => t != null,
      );
      const match = candidates.find((t) => String(t.taskId ?? "") === String(hid));
      return firstNonEmptyTrimmed(
        match?.completeNotes,
        match?.taskDecision?.reason,
        match?.taskDecision?.notes,
      );
    }
    return null;
  })();

  const approvedAttachments = (() => {
    const fromHistory = latestApprovedDecision
      ? normalizeTaskDecisionFiles(latestApprovedDecision, "APPROVE")
      : [];
    if (fromHistory.some((a) => Boolean(a.url?.trim()))) return fromHistory;

    const hid = latestApprovedTask?.id;
    if (hid) {
      const candidates = [data?.task, ...(data?.tasks ?? [])].filter(
        (t): t is GatePassTask => t != null,
      );
      const match = candidates.find((t) => String(t.taskId ?? "") === String(hid));
      const fromTask = normalizeTaskDecisionFiles(match?.taskDecision, "APPROVE");
      if (fromTask.some((a) => Boolean(a.url?.trim()))) return fromTask;
    }
    return fromHistory;
  })();
  const approvedAttachmentsWithUrl = approvedAttachments.filter((a) => Boolean(a.url?.trim()));
  const rejectAttachmentsWithUrl = rejectAttachments.filter((a) => Boolean(a.url?.trim()));
  const hasApprovedDetails =
    Boolean(firstNonEmptyTrimmed(approvedNotesResolved ?? undefined)) ||
    approvedAttachmentsWithUrl.length > 0;
  const hasRejectedDetails =
    Boolean(firstNonEmptyTrimmed(rejectReason ?? undefined)) || rejectAttachmentsWithUrl.length > 0;
  const canViewDecisionDetails =
    (usesApprovedDecisionDetails && hasApprovedDetails) || (isRejected && hasRejectedDetails);

  const getRejectAttachmentDisplayName = (att?: {
    url?: string | null;
    originalFileName?: string | null;
  }) => {
    if (!att) return "-";
    if (att.originalFileName?.trim()) return att.originalFileName;
    const url = att.url;
    if (!url) return "-";
    try {
      const withoutQuery = url.split("?")[0];
      const parts = withoutQuery.split("/");
      const last = parts[parts.length - 1];
      return last || url;
    } catch {
      return url;
    }
  };

  const gatepassFlowData: GatepassFlowData | undefined = data
    ? {
        id: data.id,
        serialNumber: data.serialNumber,
        status: effectiveStatus || data.status,
        sponsorCompany: data.sponsorCompany ?? null,
        skipSponsorCompanyApproval,
        task: activeTask
          ? {
              taskId: String(activeTask.taskId ?? ""),
              role: String(activeTask.role ?? ""),
              workflowId: String(activeTask.workflowId ?? ""),
              runId: String(activeTask.runId ?? ""),
              status: String(activeTask.status ?? ""),
              claimOwnerName: activeTask.claimOwnerName ?? null,
            }
          : null,
      }
    : undefined;

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        subtitle={
          <div
            className="flex items-center space-x-2 text-sm"
            data-testid={`${TEST_ID_BASE}-breadcrumb`}
          >
            <Link
              to={`/${paths.gatepass.index}/${paths.gatepass.gatepasses}`}
              className="text-blue-600 hover:underline"
              data-testid={`${TEST_ID_BASE}-breadcrumb-link`}
            >
              {t("gatepass.gatepass-subtitle")}
            </Link>
            <span className="text-gray-400">/</span>
            <span data-testid={`${TEST_ID_BASE}-breadcrumb-current`}>{serialNumber}</span>
          </div>
        }
        title={
          <div className="flex items-end justify-between w-full">
            <div>
              <span data-testid={`${TEST_ID_BASE}-title`}>
                {gatepassTypeLabel} {t("gatepass.gatepass-subtitle")} {serialNumber}
              </span>
              <div className="mt-2 flex items-center">
                <Badge type={badgeType} label={statusLabel} bold={false} />
                {canViewDecisionDetails && (
                  <>
                    <span className="text-base ml-2">–</span>
                    <Button
                      testId={`${TEST_ID_BASE}-view-decision-details`}
                      variant="text"
                      type="button"
                      className="ml-4 whitespace-nowrap cursor-pointer"
                      onClick={() => setShowDecisionDetailsDrawer(true)}
                      label={
                        <span className="normal-case font-medium">
                          {t("gatepass.view-details", { defaultValue: "View details" })}
                        </span>
                      }
                    />
                  </>
                )}
              </div>
            </div>
            {gatepassFlowData ? (
              <GatepassFlowButtons
                openDrawer={handleOpenDrawer}
                gatepassFlowData={gatepassFlowData}
                onPrint={handlePrint}
              />
            ) : null}
          </div>
        }
      />

      <ApproveGatepassDrawer
        isOpen={openDrawers.approveDrawer}
        onClose={handleCloseDrawer}
        gatepassFlowData={gatepassFlowData}
        setFakeStatus={setFakeStatus}
      />
      <RejectGatepassDrawer
        isOpen={openDrawers.rejectDrawer}
        onClose={handleCloseDrawer}
        gatepassFlowData={gatepassFlowData}
        setFakeStatus={setFakeStatus}
      />

      <div className="max-w-sm">
        <SideDrawer
          isOpen={showDecisionDetailsDrawer}
          title={t("gatepass.view-gatepass-details-title", {
            defaultValue: "View Gatepass Details",
          })}
          onClose={() => setShowDecisionDetailsDrawer(false)}
          submitLabel={t("common.close")}
          onSubmit={() => setShowDecisionDetailsDrawer(false)}
          hideCancelButton
        >
          <div
            className="flex flex-col gap-4 w-[400px] p-6"
            data-testid={`${TEST_ID_BASE}-decision-details-drawer-content`}
          >
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                {usesApprovedDecisionDetails
                  ? t("gatepass.notes", { defaultValue: "Notes" })
                  : t("gatepass.reject-reason", { defaultValue: "Reject reason" })}
              </CustomFormLabel>
              <div className="text-sm whitespace-pre-wrap wrap-break-word text-gray-900">
                {String(
                  usesApprovedDecisionDetails
                    ? (approvedNotesResolved ??
                        t("gatepass.no-notes", {
                          defaultValue: "No notes available.",
                        }))
                    : (rejectReason ??
                        t("gatepass.no-reject-reason", {
                          defaultValue: "No reject reason available.",
                        })),
                )}
              </div>
            </div>
            {(usesApprovedDecisionDetails
              ? approvedAttachmentsWithUrl[0]
              : rejectAttachmentsWithUrl[0]) && (
              <AttachmentsComponent
                documentName={getRejectAttachmentDisplayName(
                  usesApprovedDecisionDetails
                    ? approvedAttachmentsWithUrl[0]
                    : rejectAttachmentsWithUrl[0],
                )}
                onDownload={() => {
                  const url = usesApprovedDecisionDetails
                    ? approvedAttachmentsWithUrl[0]?.url
                    : rejectAttachmentsWithUrl[0]?.url;
                  if (url) {
                    downloadAttachment(
                      url,
                      getRejectAttachmentDisplayName(
                        usesApprovedDecisionDetails
                          ? approvedAttachmentsWithUrl[0]
                          : rejectAttachmentsWithUrl[0],
                      ),
                    );
                  }
                }}
                onPreview={() => {
                  const url = usesApprovedDecisionDetails
                    ? approvedAttachmentsWithUrl[0]?.url
                    : rejectAttachmentsWithUrl[0]?.url;
                  if (url) {
                    handlePreview(url);
                  }
                }}
                langBase="gatepass"
                documentType={t("gatepass.attachments", { defaultValue: "Attachments" })}
              />
            )}
          </div>
        </SideDrawer>
      </div>

      <div
        className="mt-8 px-24 pb-8 flex flex-col gap-4 min-h-[200px]"
        data-testid={`${TEST_ID_BASE}-content`}
      >
        <GatepassProfileSection
          testIdBase={TEST_ID_BASE}
          profileImgUrl={profileImgUrl}
          fullNameEn={fullNameEn}
          fullNameAr={fullNameAr}
          dateOfBirth={dateOfBirth}
          passportNumber={passportNumber}
          passportExpiryDate={passportExpiryDate}
          gatepassNo={gatepassNo}
        />

        <GatepassDetailsSection
          testIdBase={TEST_ID_BASE}
          title={`${gatepassTypeLabel} ${t("gatepass.gatepass-subtitle")}`}
          sections={[
            {
              leftFields: [
                { label: t("gatepass.entry-type"), value: data?.entryType ?? "-" },
                { label: t("gatepass.gatepass-duration"), value: data?.duration ?? "-" },
                {
                  label: t("gatepass.date-of-issue"),
                  value: dateOfIssue,
                },
                {
                  label: t("gatepass.date-of-expiry"),
                  value: dateOfExpiry,
                },
                { label: t("gatepass.expected-entry-date"), value: expectedEntryDate },
                {
                  label: t("gatepass.emirates-id-number"),
                  value: applicant?.emiratesIdNumber ?? "-",
                },
                ...(isOffshoreGatepass
                  ? [
                      {
                        label: t("gatepass.entry-purpose"),
                        value: entryPurpose,
                      },
                    ]
                  : []),
                {
                  label: t("gatepass.nationality-en"),
                  value: nationalityEnglish,
                },
                {
                  label: t("gatepass.nationality-ar"),
                  value: nationalityArabic,
                },
                {
                  label: t("gatepass.visitor-company-name-en"),
                  value: visitorCompanyEnglish,
                },
                {
                  label: t("gatepass.visitor-company-name-ar"),
                  value: visitorCompanyArabic,
                },
                { label: t("gatepass.sponsor-company"), value: sponsorCompanyName },
              ],
              rightFields: [
                ...(!isOffshoreGatepass
                  ? [
                      {
                        label: t("gatepass.fott-entry"),
                        value: data?.fottEntry ? t("yes") : t("no"),
                      },
                    ]
                  : []),

                ...(!isTruckGatepass
                  ? [{ label: t("gatepass.cargo-allowed"), value: t("no") }]
                  : []),
                ...(isOffshoreGatepass || isTruckGatepass
                  ? [{ label: t("gatepass.request-for"), value: data?.requestFor ?? "-" }]
                  : []),
                ...(isTruckGatepass
                  ? [
                      {
                        label: t("gatepass.truck-cargo-mode", { defaultValue: "Gatepass Type" }),
                        value: resolveTruckTypeLabel(data.details?.truckType),
                      },
                    ]
                  : []),
                {
                  label: t("gatepass.port", { defaultValue: "Port" }),
                  value: portLabel,
                },
                { label: t("gatepass.gate"), value: gateLabel },
                ...cargoDestinationFields,
                { label: t("gatepass.uid-number"), value: uidNumber },
                {
                  label: t("gatepass.occupation-en"),
                  value: occupationEnglish,
                },
                {
                  label: t("gatepass.occupation-ar"),
                  value: occupationArabic,
                },
                ...(isOffshoreGatepass
                  ? [
                      {
                        label: t("gatepass.security-clearance-duration"),
                        value: securityDuration,
                      },
                    ]
                  : []),
                ...(!isTruckGatepass
                  ? [
                      {
                        label: t("gatepass.visa-type"),
                        value: getVisaTypeLabel(visaTypeCode),
                      },
                      {
                        label: t("gatepass.visa-expiry-date"),
                        value: visaExpiryDate,
                      },
                    ]
                  : []),
                { label: t("gatepass.phone-number"), value: phoneNumber },
                ...(isTruckGatepass
                  ? [
                      {
                        label: t("gatepass.driver-license-number", {
                          defaultValue: "Driver license number",
                        }),
                        value: driverLicenseNumber,
                      },
                    ]
                  : []),
              ],
            },
            ...(isTruckGatepass
              ? [
                  {
                    subtitle: t("gatepass.job-details", { defaultValue: "Job Details" }),
                    rightSubtitle: t("gatepass.vehicle-details"),
                    leftFields: jobDetailsFields,
                    rightFields: vehicleRightFields,
                  },
                  {
                    gridItems: truckCargoGridItems,
                  },
                ]
              : [
                  {
                    subtitle: isNormalGatepass
                      ? t("gatepass.locations")
                      : t("gatepass.vessel-details"),
                    rightSubtitle: t("gatepass.vehicle-details"),
                    leftFields: isNormalGatepass
                      ? locationFields.length
                        ? locationFields
                        : [
                            {
                              label: t("gatepass.location-entry"),
                              value: "-",
                            },
                          ]
                      : vesselDetails.length
                        ? vesselFields
                        : [
                            {
                              label: t("gatepass.vessel-label"),
                              value: "-",
                            },
                          ],
                    rightFields: vehicleRightFields,
                  },
                ]),
            {
              subtitle: t("gatepass.other-data"),
              leftFields: [
                { label: t("gatepass.shelf-no"), value: "-" },
                { label: t("gatepass.issued-by-approved-by"), value: issuedByApprovedBy },
              ],
              rightFields: [
                { label: t("gatepass.ref"), value: referenceValue },
                {
                  label: t("gatepass.required-by-submitted-by", {
                    defaultValue: "Required by/Submitted by",
                  }),
                  value: submittedByDisplay,
                },
              ],
            },
          ]}
        />

        <div className="mb-8 no-border">
          <div className="mb-3 font-semibold no-break text-gray-700 text-lg">
            {attachmentsLabel}
          </div>
          <div className="border border-gray-200 rounded-md px-4 pb-1 pt-4 mb-6 mt-6 no-border no-break">
            <div
              className={`${isTruckGatepass ? "grid grid-cols-1 md:grid-cols-4 gap-6 mb-4" : "flex flex-col md:flex-row gap-6 mb-4"}`}
            >
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${passportFront?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (passportFront?.url) handlePreview(passportFront.url);
                      }}
                    >
                      {t("gatepass.view-passport-front")}
                    </a>
                  </span>
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${passportBack?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (passportBack?.url) handlePreview(passportBack.url);
                      }}
                    >
                      {t("gatepass.view-passport-back")}
                    </a>
                  </span>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${emiratesIdFront?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (emiratesIdFront?.url) handlePreview(emiratesIdFront.url);
                      }}
                    >
                      {t("gatepass.view-emirates-id-front")}
                    </a>
                  </span>
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${emiratesIdBack?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (emiratesIdBack?.url) handlePreview(emiratesIdBack.url);
                      }}
                    >
                      {t("gatepass.view-emirates-id-back")}
                    </a>
                  </span>
                </div>
              </div>
              <div className="w-full">
                <div className="flex flex-col gap-2 ml-6">
                  <span className="block">
                    <a
                      href="#"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${visa.length && visa[0]?.url ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (visa.length && visa[0]?.url) handlePreview(visa[0].url);
                      }}
                    >
                      {t("gatepass.view-visa")}
                    </a>
                  </span>
                </div>
              </div>
              {isTruckGatepass && (
                <div className="w-full">
                  <div className="flex flex-col gap-2 ml-6">
                    {deliveryNotes.length ? (
                      deliveryNotes.map((note, idx) => {
                        const hasUrl = Boolean(note?.url?.trim());
                        const label = t(
                          deliveryNotes.length > 1
                            ? "gatepass.delivery-note-with-number"
                            : "gatepass.delivery-note",
                          {
                            number: idx + 1,
                          },
                        );

                        return (
                          <span className="block" key={`${note.type}-${idx}`}>
                            <a
                              href="#"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${hasUrl ? "text-blue-600 underline cursor-pointer" : "text-gray-500 cursor-not-allowed"} font-bold`}
                              onClick={(e) => {
                                e.preventDefault();
                                if (note?.url) handlePreview(note.url);
                              }}
                            >
                              {label}
                            </a>
                          </span>
                        );
                      })
                    ) : (
                      <span className="block text-gray-500 font-bold">
                        {t("gatepass.all-delivery-notes", { defaultValue: "All delivery notes" })}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default GatepassRequestPreview;
